import type { ReactNode } from 'react';
import { ActionableNotification } from '@carbon/react';

import { usePackageWizardCtx } from './context';
import styles from './export-notice.module.scss';

/**
 * The Step 1 export's message: the file is ready, with Download, or the export
 * failed, with Try again. It stays until the user acts on it, closes it or
 * starts another export. The file opens from the Download click, so pop-up
 * blockers let it through. Both are `alert`: the message is inserted holding
 * its text, which screen readers announce for an alert but often skip for a
 * status, and it is the only sign the file is ready. Not Carbon's default
 * `alertdialog`, which takes and traps focus: the message can arrive while the
 * user works on another step.
 *
 * @return {JSX.Element | null} The message, or nothing
 */
function ExportNotice() {
  const { notice, runExport, dismissNotice } =
    usePackageWizardCtx().backgroundDownload;

  if (notice?.kind === 'ready') {
    return (
      <ActionableNotification
        inline
        role="alert"
        kind="success"
        lowContrast
        title="Your export is ready"
        actionButtonLabel="Download"
        onActionButtonClick={() => {
          window.open(notice.fileUrl, '_blank', 'noopener,noreferrer');
          dismissNotice();
        }}
        onCloseButtonClick={dismissNotice}
        className={styles.notice}
      />
    );
  }

  if (notice?.kind === 'failed') {
    return (
      <ActionableNotification
        inline
        role="alert"
        kind="error"
        lowContrast
        title="Export failed"
        actionButtonLabel="Try again"
        onActionButtonClick={() => void runExport(notice.href, notice.body)}
        onCloseButtonClick={dismissNotice}
        className={styles.notice}
      />
    );
  }

  return null;
}

/**
 * A wizard step with the export's message above it, so the message shows on
 * whichever step is open. The step keeps its place in the tree whether or not
 * a message shows, and takes the height the message leaves.
 *
 * @param {object} props - The step content
 * @return {JSX.Element} The message, then the step
 */
export function WithExportNotice({ children }: { children: ReactNode }) {
  return (
    <div className={styles.step}>
      <ExportNotice />
      <div className={styles.stepBody}>{children}</div>
    </div>
  );
}
